import { forwardRef, useId, type InputHTMLAttributes } from 'react'
import { Check, Minus } from 'lucide-react'
import { cn } from '@/internationalization/cn'

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label?: string
  description?: string
  /** Header checkbox of a partially selected table. */
  indeterminate?: boolean
}

export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(function Checkbox(
  { label, description, indeterminate, className, id, ...props },
  ref,
) {
  const autoId = useId()
  const boxId = id ?? autoId
  return (
    <div className={cn('flex items-start gap-2.5', className)}>
      <span className="relative flex h-5 items-center">
        <input
          ref={ref}
          id={boxId}
          type="checkbox"
          aria-checked={indeterminate ? 'mixed' : undefined}
          className="peer h-4 w-4 shrink-0 cursor-pointer appearance-none rounded-sm border border-border-strong
            bg-surface transition-colors duration-fast checked:border-brand checked:bg-brand
            disabled:cursor-not-allowed disabled:opacity-50"
          {...props}
        />
        <span
          aria-hidden
          className="pointer-events-none absolute left-0 top-1/2 flex h-4 w-4 -translate-y-1/2 items-center
            justify-center text-brand-fg opacity-0 peer-checked:opacity-100"
        >
          {indeterminate ? <Minus className="h-3 w-3" /> : <Check className="h-3 w-3" strokeWidth={3} />}
        </span>
      </span>
      {(label || description) && (
        <span className="min-w-0">
          {label && (
            <label htmlFor={boxId} className="block cursor-pointer text-base text-fg">
              {label}
            </label>
          )}
          {description && <span className="mt-0.5 block text-sm text-fg-subtle">{description}</span>}
        </span>
      )}
    </div>
  )
})
